import { useEffect, useRef } from "react";
import styled from "@emotion/styled";

import {
  Sidebar,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
  useSidebar,
} from "components/ui/sidebar/Sidebar";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useIsLaptopLgUp, useIsMobileOrTablet } from "lib/providers/Responsive";
import Content from "./Content";
import Footer from "./Footer";
import Header from "./Header";
import MobileTopNav from "./MobileTopNav";
import { useSideNavOpenState } from "./useSideNavOpenState";

type VerticalNavProps = {
  hideNavBar?: boolean; // Completely hide nav bar
  routePathname?: string; // Current SPA route path; drives collapse-on-navigate below laptop-lg. Undefined on legacy (non-SPA) pages.
  children: React.ReactNode;
};

// The page content, beside the nav. While the mobile panel is open it's hidden (so the panel
// takes the remaining height) and marked inert, so keyboard/AT users can't reach content behind
// the menu (WCAG 2.4.11). Hidden rather than unmounted so page state and scroll survive.
const NavPageContent = ({
  routePathname,
  children,
}: {
  routePathname?: string;
  children: React.ReactNode;
}) => {
  const { isMobile, openMobile, setOpenMobile } = useSidebar();
  const contentRef = useRef<HTMLElement>(null);
  const isCoveredByMobileMenu = isMobile && openMobile;

  // Close the mobile overlay on SPA navigation: it covers the page, so any navigate from it should
  // dismiss it. Lives here, not in the Sidebar primitive, so route awareness stays in the nav layer.
  useEffect(() => {
    setOpenMobile(false);
  }, [routePathname, setOpenMobile]);

  // inert isn't in the repo's React 18 JSX types, so set it via ref rather than a cast.
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    if (isCoveredByMobileMenu) {
      element.setAttribute("inert", "");
    } else {
      element.removeAttribute("inert");
    }
  }, [isCoveredByMobileMenu]);

  return (
    <NavPageInset ref={contentRef} isCovered={isCoveredByMobileMenu}>
      {children}
    </NavPageInset>
  );
};

// Gates the open-panel layout change behind a prop rather than a per-render inline style object.
const NavPageInset = styled(SidebarInset, {
  shouldForwardProp: (prop) => prop !== "isCovered",
})<{ isCovered: boolean }>(({ isCovered }) => (isCovered ? { display: "none" } : {}));

const VerticalNavInternal = () => {
  const { data: currentUser } = useCurrentUser();
  const { isMobile } = useSidebar();

  // No currentUser means an anonymous viewer of a public route (report / lead URLs need no auth).
  const isGuestUser = !currentUser;

  return (
    <Sidebar
      side="left"
      variant="sidebar"
      collapsible="icon"
      iconHover="overlay"
      aria-label="Main Navigation"
    >
      {/* The account dropdown duplicates Settings, which the mobile menu lists directly, and the
          top bar already shows the logo — so it's desktop-only. Logout moves to the mobile footer. */}
      {!isMobile && <Header isGuestUser={isGuestUser} />}
      <Content isGuestUser={isGuestUser} />
      <Footer isGuestUser={isGuestUser} />
      {!isMobile && (
        <SidebarRail
          // To prevent the rail from triggering the hover open sidebar state
          // Not stopping onMouseClose on purpose so that it can still close when moving away
          onMouseEnter={(e) => {
            e.stopPropagation();
          }}
        />
      )}
    </Sidebar>
  );
};

export const VerticalNavNew = ({ hideNavBar, routePathname, children }: VerticalNavProps) => {
  const isMobile = useIsMobileOrTablet();
  const isLaptopLgUp = useIsLaptopLgUp();

  // Side nav is visible but not persistent at 1024–1439: collapse on navigate unless pinned.
  // Derived from tier boundaries so inserting a tier in this range can't silently drop it.
  const collapseOnNavigate = !isMobile && !isLaptopLgUp;
  const { open, onOpenChange } = useSideNavOpenState({ collapseOnNavigate, routePathname });

  if (hideNavBar) return children;

  return (
    <SidebarProvider
      sidebarKey="verticalNav"
      keyboardShortcut="\"
      isMobile={isMobile}
      open={open}
      onOpenChange={onOpenChange}
    >
      {isMobile && <MobileTopNav />}
      <VerticalNavInternal />
      <NavPageContent routePathname={routePathname}>{children}</NavPageContent>
    </SidebarProvider>
  );
};
